import config from 'constants/config'
import { isItemCancelled, isTourV2OrderItem } from 'lib/order/orderUtils'
import { ITEM_STATUS_AWAITING_DATES } from 'constants/cart'

/**
 * Pure eligibility check for the TripChat-from-order flow.
 * Does NOT check config — use `isOrderSupportedForChatTripGeneration` for config-gated checks.
 */
export function isOrderEligibleForChatTrip(order: App.Order): boolean {
  const isTourOrder = order.items.some(item => isTourV2OrderItem(item))
  if (isTourOrder) return false

  const hasAnyBNBLItems = order.items.some(
    item => item.status === ITEM_STATUS_AWAITING_DATES || !item.reservationMade,
  )
  if (hasAnyBNBLItems) return false

  if (order.orderGift) return false

  return true
}

function hasActiveOrderItems(items?: Array<unknown>): boolean {
  return items?.some(item => !isItemCancelled(item as App.AnyOrderItem)) ?? false
}

/**
 * Whole-order add-booking chat context currently supports only hotel, flight,
 * and experience items. Exclude orders that also contain other active item
 * types so the generated context matches the traveller's selected booking.
 */
export function isOrderSupportedForChatBookingAddition(order: App.Order): boolean {
  const hasSupportedBookingItems =
    hasActiveOrderItems(order.items) ||
    hasActiveOrderItems(order.bedbankItems) ||
    hasActiveOrderItems(order.flightItems) ||
    hasActiveOrderItems(order.experienceItems)

  const hasUnsupportedBookingItems =
    hasActiveOrderItems(order.tourItems) ||
    hasActiveOrderItems(order.cruiseItems) ||
    hasActiveOrderItems(order.carHireItems) ||
    hasActiveOrderItems(order.transferItems) ||
    hasActiveOrderItems(order.customOfferItems) ||
    hasActiveOrderItems(order.giftCardItems) ||
    hasActiveOrderItems(order.insuranceItems) ||
    hasActiveOrderItems(order.addonItems) ||
    hasActiveOrderItems(order.merchandiseItems) ||
    hasActiveOrderItems(order.merchandiseShippingItems)

  return hasSupportedBookingItems && !hasUnsupportedBookingItems
}

/**
 * Config-gated eligibility check for the TripChat-from-order flow.
 *
 * More permissive than `useOrderSupportsTripGeneration` (the classic V1 flow):
 * - Supports multi-hotel orders (no single-accommodation restriction)
 * - No geocoding / blacklist check needed
 */
export function isOrderSupportedForChatTripGeneration(order: App.Order): boolean {
  if (!config.ENABLE_TRIP_PLANNER) return false

  return isOrderEligibleForChatTrip(order)
}
